import ReactDOM from 'react-dom';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import ScrollTop from 'src/hooks/useScrollTop';

import 'nprogress/nprogress.css';
import { Provider } from 'react-redux';
import store from 'src/store';
import App from 'src/App';
import { SidebarProvider } from 'src/contexts/SidebarContext';
import * as serviceWorker from 'src/serviceWorker';
import { AuthProvider } from 'src/contexts/FirebaseAuthContext';
import SocketClient from './api/SocketClient';
import AppErrorBoundary from './components/AppErrorBoundary';
import { initializeWebErrorTelemetry } from './utils/errorTelemetry';
import { markStartup } from './utils/startupPerformance';

export const socketClient = new SocketClient();

initializeWebErrorTelemetry();
markStartup('script-ready');

const CHUNK_RECOVERY_KEY = 'autoclever.chunkRecovery.v1';
const recoverFromStaleChunk = (error) => {
  const message = String(error?.message || error || '');
  if (!/(?:ChunkLoadError|Loading (?:CSS )?chunk [^ ]+ failed|Failed to fetch dynamically imported module)/i.test(message)) {
    return false;
  }
  const lastReloadAt = Number(window.sessionStorage.getItem(CHUNK_RECOVERY_KEY) || 0);
  if (Date.now() - lastReloadAt < 5 * 60 * 1000) {
    return false;
  }
  window.sessionStorage.setItem(CHUNK_RECOVERY_KEY, String(Date.now()));
  window.location.reload();
  return true;
};

window.addEventListener('error', (event) => {
  recoverFromStaleChunk(event.error || event.message);
});
window.addEventListener('unhandledrejection', (event) => {
  recoverFromStaleChunk(event.reason);
});

const loadChartEnhancements = () => import('src/utils/chart').catch(() => undefined);
if (typeof window.requestIdleCallback === 'function') {
  window.requestIdleCallback(loadChartEnhancements, { timeout: 2000 });
} else {
  window.setTimeout(loadChartEnhancements, 0);
}

ReactDOM.render(
  <HelmetProvider>
    <Provider store={store}>
      <SidebarProvider>
        <BrowserRouter>
          <ScrollTop />
          <AuthProvider>
            <AppErrorBoundary>
              <App />
            </AppErrorBoundary>
          </AuthProvider>
        </BrowserRouter>
      </SidebarProvider>
    </Provider>
  </HelmetProvider>,
  document.getElementById('root')
);

serviceWorker.unregister();
